import {
  EventHeaderCard,
  PhaseStepper,
  StatusPill,
  formatEventDateTime,
  readerTimezone,
} from '@h2hspark/ui';
import { ArrowRight, ChevronRight, MapPin, Sparkles, UserPlus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useMyConnectionRequests } from '../connections/api';
import { TicketDialog } from '../event-tickets/ticket-dialog';
import { useFollowUpForEvent } from '../follow-up/api';
import { useMyMatchSet } from '../matching/api';
import { useConnectSurface } from '../member-in-event/api';

import { AddToCalendarControl } from './add-to-calendar-panel';
import { CancelRegistrationControl } from './cancel-registration-panel';
import {
  EVENT_DETAILS_LINK,
  MATCH_ALERT_ACTION,
  MATCH_ALERT_DESCRIPTION,
  REQUESTS_CONTROL_LABEL,
  VIEW_TICKET_LINK,
  eventPageRequestsPath,
  eventPhaseParts,
  matchAlertTitle,
  registeredStatus,
  requestsControlName,
  type ConnectFact,
  type FollowUpFact,
  type MatchesFact,
} from './constants';

/** The event fields the header card reads — structurally the public event payload's. */
export interface RegisteredEventHeaderEvent {
  readonly eventId: string;
  readonly name: string;
  readonly startsAt: string;
  readonly endsAt: string | null;
  readonly timezone: string | null;
  readonly hasEnded: boolean;
  readonly location: { readonly city: string | null; readonly venueName: string | null };
}

/**
 * **Which of the five pages this card is heading** (`MEM-053` AC-1).
 *
 * `event` is the event page itself: its title is plain text (the reader is already there), its
 * link row is *Add to calendar · View your ticket*, cancelling sits under the pill, and no part of
 * the stepper is active. Every other value is one of the per-event pages the plan's Phase 3 builds:
 * the title becomes a link back to the event with the prototype's `chevron-right`, the link row
 * becomes *View full event details*, and the stepper marks the page the member is on.
 *
 * `requests` marks no phase — the prototype's requests page draws the stepper with nothing active
 * (§2.5, item 1), because connection requests are not one of the three phases.
 */
export type EventHeaderSurface =
  | 'event'
  | 'attendees'
  | 'matches'
  | 'requests'
  | 'connect'
  | 'follow-up';

const ACTIVE_PHASE: Readonly<Record<EventHeaderSurface, 'before' | 'during' | 'after' | undefined>> =
  {
    event: undefined,
    attendees: undefined,
    matches: 'before',
    requests: undefined,
    connect: 'during',
    'follow-up': 'after',
  };

/**
 * **The event header card, for a member with a confirmed registration** (`MEM-053` AC-1, AC-2,
 * AC-3, AC-4, AC-6), built from `Web App.dc.html` lines 264–276 and §3.3–§3.5.
 *
 * The caller renders it only for a confirmed registration (`MEM-053` AC-5: a member who is not
 * registered, one whose registration was cancelled or refunded, and the organizer's Review step
 * see the unregistered page). Every fact on it is the member's own, read from the endpoint that
 * owns it — nothing here is inferred from the clock that a server has a rule for:
 *
 *   - **the stepper**: the match set (`GET /api/matching/events/:id/mine`), the Connect surface's
 *     own open-or-closed answer (`GET /api/member-in-event/events/:id/connect`), and the
 *     follow-up summary (`GET /api/follow-up/events/:id`); see `eventPhaseParts`;
 *   - **the status pill**: `registeredStatus`, which will not say *Attended* without the summary a
 *     check-in produces;
 *   - **Requests**: the member's pending inbound requests (`GET /api/connections/requests`), which
 *     each carry the event they came from, counted for this event. The control is drawn only while
 *     that count is above zero, and it opens the requests screen — the per-event requests page the
 *     prototype draws is the plan's Phase 3.
 *
 * The date line is the event's own, in its own zone, with the abbreviation where it differs from
 * the reader's (`MEM-015` AC-2) — `formatEventDateTime`, the one the whole app reads dates with.
 */
export function RegisteredEventHeader({
  event,
  confirmationCode,
  surface = 'event',
}: {
  readonly event: RegisteredEventHeaderEvent;
  /** The member's own registration code, for the calendar entry (`MEM-017` AC-2). */
  readonly confirmationCode: string;
  /** Which of `MEM-053` AC-1's five pages this is heading. Defaults to the event page. */
  readonly surface?: EventHeaderSurface;
}): React.ReactElement {
  const reader = React.useMemo(() => readerTimezone(), []);
  const matches = useMatchesFact(event.eventId);
  const connect = useConnectFact(event.eventId, event.hasEnded);
  const followUp = useFollowUpFact(event.eventId, event.hasEnded);
  const requests = useMyConnectionRequests({ enabled: true });
  const requestCount = (requests.data?.requests ?? []).filter(
    (request) => request.eventId === event.eventId,
  ).length;

  const onEventPage = surface === 'event';
  /* The attendee list ("See all" under Who's coming) opens with the event page's card, as Brad
     drew it on 2026-09-25 (TW 44189308): the name as text and *View your ticket* under the date.
     Cancelling stays the event page's alone. */
  const detailVariant = onEventPage || surface === 'attendees';
  const eventPath = `/app/events/${encodeURIComponent(event.eventId)}`;
  const status = registeredStatus({ hasEnded: event.hasEnded, followUp });
  const parts = eventPhaseParts({
    eventId: event.eventId,
    hasEnded: event.hasEnded,
    matches,
    connect,
    followUp,
    active: ACTIVE_PHASE[surface],
  });
  const date = formatEventDateTime({
    startsAt: event.startsAt,
    timezone: event.timezone,
    reader,
    style: 'short',
  });
  const place = event.location.city ?? event.location.venueName;

  return (
    <EventHeaderCard
      startsAt={event.startsAt}
      timezone={event.timezone}
      title={
        /* §3.4: the name *"gets `cursor:pointer` and `chevron-right` 18 brand-primary on every
           variant except detail"*. On the event page the reader is already there, so it is text. */
        detailVariant ? (
          event.name
        ) : (
          <Link to={eventPath} className="inline-flex items-center gap-[7px]">
            {event.name}
            <ChevronRight className="h-[18px] w-[18px] shrink-0 text-primary" aria-hidden />
          </Link>
        )
      }
      meta={
        <>
          <span>{date}</span>
          {place === null ? null : (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {place}
            </span>
          )}
        </>
      }
      link={
        /* §3.4: *"View your ticket" (detail) or "View full event details" (others)*. A per-event
           page's one link is the way back to the whole evening; the ticket and the calendar entry
           belong to the event page, which is where they were asked for (Sumit, #528). */
        !detailVariant ? (
          <Link
            to={eventPath}
            className="inline-flex items-center gap-[5px] font-brand text-sm font-bold text-primary"
          >
            {EVENT_DETAILS_LINK}
            <ArrowRight className="h-[15px] w-[15px]" aria-hidden />
          </Link>
        ) : (
        /* The row under the date: the calendar entry, then the ticket, in the prototype's text-link
           treatment (§3.6). Sumit, reviewing #528, asked for the calendar here and to the left of
           the ticket; his ruling of 2026-09-03 — *"make sure View ticket is still reachable from
           the event page"* — holds after the event too, so the ticket control is on the card in
           every state. */
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {/* Not for an evening that is over: an `.ics` for last Thursday is the control
              `MEM-015` AC-7 takes off the page. */}
          {event.hasEnded ? null : (
            <AddToCalendarControl
              eventName={event.name}
              startsAt={event.startsAt}
              endsAt={event.endsAt}
              timezone={event.timezone}
              venueName={event.location.venueName}
              confirmationCode={confirmationCode}
            />
          )}
          {/* The prototype opens the ticket as an overlay rather than as a page (`ticketOpen`).
              `/app/events/:id/ticket` still resolves — mailed links point at it. */}
          <TicketDialog eventId={event.eventId}>
            <button
              type="button"
              className="inline-flex items-center gap-[5px] font-brand text-sm font-bold text-primary"
            >
              {VIEW_TICKET_LINK}
              <ArrowRight className="h-[15px] w-[15px]" aria-hidden />
            </button>
          </TicketDialog>
        </span>
        )
      }
      aside={
        /* **The Requests control and the status pill on one row, then cancelling beneath the
           pill** — Sumit, reviewing #529 (ruling 30): the two were on different baselines.

           They were siblings of a column that also held *Cancel my registration*, so the card's
           `items-center` centred the 40px button against the **whole** column — pill plus link —
           and it sat a few pixels low. One row for the two things that are the same kind of thing,
           and the quiet link under it. */
        <span className="flex flex-col items-end gap-1.5">
          <span className="flex flex-wrap items-center justify-end gap-2.5">
          {/* [MEM-053-AC-6]: only while this event has pending requests for the member, and never
              on the requests page itself, where the prototype draws a control that links to the
              page it is on. It opens **this event's** requests, because the count on it is this
              event's — sending a member holding "2" to a list of six would make the number wrong at
              the destination (the global list stays, reached from Connections; `MEM-032` AC-6). */}
          {requestCount === 0 || surface === 'requests' ? null : (
            <Link
              to={eventPageRequestsPath(event.eventId)}
              aria-label={requestsControlName(requestCount)}
              className="inline-flex flex-none items-center gap-[7px] whitespace-nowrap rounded-pill border-[1.5px] border-primary bg-card px-4 py-2.5 font-brand text-sm font-bold text-primary"
            >
              <UserPlus className="h-4 w-4" aria-hidden />
              {REQUESTS_CONTROL_LABEL}
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-brand-accent px-1.5 text-[11px] font-black text-white">
                {requestCount}
              </span>
            </Link>
          )}
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          </span>
          {/* Cancelling as a quiet line under the pill — Sumit, reviewing #528. Not offered for an
              evening that is over: there is no seat to release and no refund window left open
              (`MEM-023`), and the API refuses it. */}
          {event.hasEnded || !onEventPage ? null : (
            <CancelRegistrationControl eventId={event.eventId} eventName={event.name} />
          )}
        </span>
      }
    >
      <PhaseStepper
        parts={parts}
        renderLink={({ href, className, children }) => (
          <Link to={href} className={className}>
            {children}
          </Link>
        )}
      />
    </EventHeaderCard>
  );
}

/**
 * **We found N matches for you** — the prototype's info band (§3.24) beneath the host row, with
 * the member's real count, only once the set is released and before the event ends
 * (`matchAlertTitle`). Nothing at all otherwise, rather than an alert about nothing.
 */
export function MatchAlert({
  eventId,
  hasEnded,
}: {
  readonly eventId: string;
  readonly hasEnded: boolean;
}): React.ReactElement | null {
  const matches = useMatchesFact(eventId);
  const title = matchAlertTitle({ hasEnded, matches });
  if (title === null) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-3.5 rounded-lg border border-brand-100 bg-brand-50 px-[18px] py-4"
      data-testid="match-alert"
    >
      <span
        className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-pill bg-spark text-white"
        aria-hidden
      >
        <Sparkles className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1 basis-48">
        <p className="font-brand text-md font-bold text-foreground">{title}</p>
        <p className="mt-px text-sm text-muted-foreground">{MATCH_ALERT_DESCRIPTION}</p>
      </div>
      <Link
        to={`/app/events/${encodeURIComponent(eventId)}/matches`}
        className="inline-flex flex-none items-center gap-[7px] whitespace-nowrap rounded-pill bg-primary px-[18px] py-[11px] font-brand text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {MATCH_ALERT_ACTION}
        <ArrowRight className="h-[15px] w-[15px]" aria-hidden />
      </Link>
    </div>
  );
}

/** The match set, as the stepper and the alert both read it — one query, shared by key. */
function useMatchesFact(eventId: string): MatchesFact {
  const query = useMyMatchSet(eventId, { enabled: true });
  if (query.data === undefined) return { kind: 'unknown' };
  const set = query.data.matchSet;
  return set === null ? { kind: 'not-released' } : { kind: 'released', count: set.suggestions.length };
}

/**
 * The Connect surface's own answer. Not asked once the event has ended — `MEM-031` AC-7 has closed
 * it for everybody and the stepper says so without a request.
 */
function useConnectFact(eventId: string, hasEnded: boolean): ConnectFact {
  const query = useConnectSurface(eventId, { enabled: !hasEnded });
  if (hasEnded) return { kind: 'closed', reason: 'event-over' };
  const surface = query.data?.surface;
  if (surface === undefined) return { kind: 'unknown' };
  return surface.state === 'open' ? { kind: 'open' } : { kind: 'closed', reason: surface.reason };
}

/** The follow-up summary. Not asked before the event ends: it is generated after (`MEM-035`). */
function useFollowUpFact(eventId: string, hasEnded: boolean): FollowUpFact {
  const query = useFollowUpForEvent(eventId, { enabled: hasEnded });
  if (!hasEnded) return { kind: 'none' };
  if (query.data === undefined) return { kind: 'unknown' };
  return query.data.followUp === null ? { kind: 'none' } : { kind: 'ready' };
}
